import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  UserKeys,
  getDomainsQuery,
  runAllContactSyncs,
  runContactSync,
} from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { getContactSyncsList } from '../../../api/users-api';
import { ContactSyncModel } from '../../../../types';
import { useMemo, useState } from 'react';
import DeleteContactSyncDialog from './delete-contact-sync-dialog';
import DeleteLabelsDialog from './delete-labels';
import { useStore } from '../../state/zustand-store';
import ContactSyncDetailsDrawer from './contact-sync-details-drawer';
import ContactSyncDetailsDialog from './contact-sync-details-dialog';
import ContactSyncUserStatusDialog from './contact-sync-user-status-dialog';
import ContactSyncGridToolbar from './contact-sync-grid-toolbar';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { pagesConfig } from '../../../shared/pages-config';

import ContactSyncDialog from './create-contact-sync/contact-sync-dialog';
import ContactSyncRunLogs from './contact-sync-run-logs';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import AvatarChip from '../../../shared/avatar-chip';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import { isFeatureEnabled } from '../../../utils/feature-flags';

import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Close from '@mui/icons-material/Close';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import Person from '@mui/icons-material/Person';
import PersonOff from '@mui/icons-material/PersonOff';
import PlayArrow from '@mui/icons-material/PlayArrow';

export default function ContactSyncGrid() {
  const { directoryRoles, changeLogRoles, viewType, features } =
    useUserSettings();
  const uiEnhancementFeatureEnabled = isFeatureEnabled(
    'uiEnhancementPhase2',
    features
  );

  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const [openCreateContactSync, setOpenCreateContactSync] = useState(false);
  const [openEditContactSync, setOpenEditContactSync] = useState(false);
  const [selectedContactSync, setSelectedContactSync] =
    useState<ContactSyncModel>();
  const [openDeleteLabels, setOpenDeleteLabels] = useState(false);
  const [openContactSyncUserStatus, setOpenContactSyncUserStatus] =
    useState(false);
  const [openContactSyncDetails, setOpenContactSyncDetails] = useState(false);
  const [openDeleteContactSync, setOpenDeleteContactSync] = useState(false);
  const [openRunLogs, setOpenRunLogs] = useState(false);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [UserKeys.ContactSyncs],
    queryFn: () => getContactSyncsList(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('contact syncs'),
    },
  });

  const { data: domains } = useQuery(getDomainsQuery());

  const { mutate, isPending: isRunningAll } = useMutation({
    mutationFn: () => runAllContactSyncs(),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The task to run all contact syncs has begun.',
      });
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Running('all contact syncs'),
      });
    },
  });

  const { mutate: runNowMutate } = useMutation({
    mutationFn: (data: ContactSyncModel) => runContactSync(data),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The task to run the contact sync has begun.',
      });
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Running('the contact sync'),
      });
    },
  });

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'createdTime',
        headerName: 'Created Date',
        flex: 1,
        display: 'flex',
        type: 'dateTime',
        valueGetter: (params: number) => {
          return new Date(params);
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'name',
        headerName: 'Sync Name',
        flex: 0.75,
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
      },
      {
        field: 'syncType',
        headerName: 'Sync Type',
        flex: 1,
        type: 'singleSelect',
        valueOptions: [
          { label: 'Directory Contacts', value: 'directory' },
          { label: 'Personal Contacts', value: 'contact' },
          { label: 'Bi-Directional Sync', value: 'bidirectional' },
        ],
        renderCell: (params: GridRenderCellParams) => {
          switch (params.row.syncType) {
            case 'directory':
              return 'Directory Contacts';
            case 'bidirectional':
              return 'Bi-Directional Sync';
            default:
              return 'Personal Contacts';
          }
        },
      },
      {
        field: 'targetGroupName',
        headerName: 'Target Label Name',
        flex: 1,
      },
      {
        field: 'groupTitle',
        headerName: 'Source Label',
        width: 150,
        renderCell: (params: GridRenderCellParams) => {
          if (params.value && params.row.syncType !== 'bidirectional') {
            return params.value;
          } else if (params.row.syncType !== 'bidirectional') {
            return 'Directory Contacts';
          } else {
            return 'Bi-Directional Sync';
          }
        },
      },
      {
        field: 'sourceUser',
        headerName: 'Source User',
        flex: 1,
        display: 'flex',
        renderCell: (params: GridRenderCellParams) => {
          if (params.value && params.row.syncType !== 'bidirectional') {
            return (
              <AvatarChip
                type="User"
                key={params.value}
                email={params.value}
                size="small"
              />
            );
          } else if (params.row.syncType !== 'bidirectional') {
            return 'Directory Contacts';
          } else {
            return 'Bi-Directional Sync';
          }
        },
      },
      {
        field: 'targets',
        headerName: 'Targets',
        flex: 1,
        display: 'flex',
        renderCell: (params: GridRenderCellParams) => {
          if (params.value) {
            return (
              <Stack flexDirection="row" gap={1} sx={{ overflowX: 'auto' }}>
                {params.value.map((target: string) => (
                  <AvatarChip
                    key={target}
                    email={target}
                    type={
                      params.row.sourceType === 'User'
                        ? 'User'
                        : params.row.sourceType === 'Group'
                        ? 'Group'
                        : 'Domain'
                    }
                  />
                ))}
              </Stack>
            );
          } else {
            return 'N/A';
          }
        },
      },
      {
        field: 'lastRunTime',
        headerName: 'Last Run Date',
        width: 160,
        type: 'dateTime',
        valueGetter: (value: number | null) => {
          if (!value) {
            return null;
          } else {
            const date = new Date(value);
            return date;
          }
        },
        valueFormatter: (params: Date | null) => {
          if (!params) {
            return 'Never Ran';
          }
          const date = new Date(params);
          return date.toLocaleString();
        },
      },
      {
        field: 'scheduled',
        headerName: 'Scheduled',
        width: 110,
        type: 'boolean',
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params) => {
          const buttonArray = [];
          buttonArray.push(
            <GridActionsCellItem
              label="Run"
              icon={
                <Tooltip arrow title="Run">
                  <PlayArrow />
                </Tooltip>
              }
              onClick={() => {
                runNowMutate(params.row);
              }}
            />
          );
          if (
            isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditContactSync.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Edit"
                icon={
                  <Tooltip arrow title="Edit">
                    <Edit />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedContactSync(params.row);
                  setOpenEditContactSync(true);
                }}
                showInMenu
              />
            );
          }
          if (
            isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.DeleteContactSync.value
            ) &&
            params.row.targets
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Delete"
                icon={
                  <Tooltip arrow title="Delete">
                    <Delete />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedContactSync(params.row);
                  setOpenDeleteContactSync(true);
                }}
                showInMenu
              />
            );
          }
          if (
            isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.RemoveContactSyncLabel.value
            ) &&
            params.row.targets
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Delete Labels"
                icon={
                  <Tooltip arrow title="Delete Labels">
                    <Close />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedContactSync(params.row);
                  setOpenDeleteLabels(true);
                }}
                showInMenu
              />
            );
          }
          buttonArray.push(
            <GridActionsCellItem
              label="View User Status"
              icon={
                <Tooltip arrow title="View User Status">
                  <Person />
                </Tooltip>
              }
              onClick={() => {
                setSelectedContactSync(params.row);
                setOpenContactSyncUserStatus(true);
              }}
              showInMenu
            />
          );
          buttonArray.push(
            <GridActionsCellItem
              label="View Run Logs"
              icon={
                <Tooltip arrow title="View Run Logs">
                  <FormatListBulleted />
                </Tooltip>
              }
              onClick={() => {
                setSelectedContactSync(params.row);
                setOpenRunLogs(true);
              }}
              showInMenu
            />
          );
          return buttonArray;
        },
      },
    ],
    [
      directoryRoles,
      runNowMutate,
      setSelectedContactSync,
      setOpenEditContactSync,
      setOpenDeleteContactSync,
      setOpenDeleteLabels,
      setOpenContactSyncUserStatus,
      setOpenRunLogs,
    ]
  );

  const defaultGridState = {
    columns: {
      columnVisibilityModel: {
        description: false,
        syncType: false,
      },
    },
    sorting: {
      sortModel: [
        {
          field: 'createdTime',
          sort: 'desc',
        },
      ],
    },
  };

  const toolbarProps = useMemo(
    () => ({
      directoryRoles: directoryRoles,
      setOpenCreateContactSync,
      mutate: mutate,
      isRunningAll: isRunningAll,
    }),
    [directoryRoles, setOpenCreateContactSync, mutate, isRunningAll]
  );

  return (
    <SimpleBreadcrumbsPage
      breadcrumbs={[
        { label: ModuleList.modules.automation.name },
        { label: pagesConfig.contactSync.name },
      ]}
      bookmarkKey={pagesConfig.contactSync.value}
    >
      <SharedDataGridClient
        gridKey={viewType.contactSyncsGridStates?.activeGridState}
        slots={{
          toolbar: useToolbar(ContactSyncGridToolbar, toolbarProps),
          ...createGridOverlays(
            'Contact Syncs',
            <PersonOff />,
            'Try changing your search term.'
          ),
        }}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        rows={data || []}
        columns={columns}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        loading={isLoading || isFetching}
        gridStateKey="contactSyncsGridStates"
        defaultGridState={defaultGridState}
        onRowClick={(params) => {
          setSelectedContactSync(params.row);
          setOpenContactSyncDetails(true);

          createEvent(
            analytics.directory.contact_sync.viewed_contact_sync_details,
            {
              module: ModuleList.modules.directory.name,
              section: ModuleList.modules.directory.sections.contactSync,
            }
          );
        }}
      />
      {selectedContactSync && openDeleteContactSync ? (
        <DeleteContactSyncDialog
          open={openDeleteContactSync}
          handleClose={() => setOpenDeleteContactSync(false)}
          contactSyncList={data || []}
          selectedContactSync={selectedContactSync}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {openCreateContactSync && (
        <ContactSyncDialog
          open={openCreateContactSync}
          domains={domains || []}
          handleClose={() => setOpenCreateContactSync(false)}
          contactSyncList={data || []}
          changeLogRoles={changeLogRoles}
          dialogType="create"
          selectedContactSync={null}
        />
      )}
      {openEditContactSync && selectedContactSync ? (
        <ContactSyncDialog
          open={openEditContactSync}
          domains={domains || []}
          handleClose={() => setOpenEditContactSync(false)}
          contactSyncList={data || []}
          changeLogRoles={changeLogRoles}
          dialogType="edit"
          selectedContactSync={selectedContactSync}
        />
      ) : null}
      {openDeleteLabels && selectedContactSync ? (
        <DeleteLabelsDialog
          open={openDeleteLabels}
          handleClose={() => setOpenDeleteLabels(false)}
          selectedContactSync={selectedContactSync}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {openContactSyncDetails && selectedContactSync ? (
        uiEnhancementFeatureEnabled ? (
          <ContactSyncDetailsDrawer
            open={openContactSyncDetails}
            handleClose={() => setOpenContactSyncDetails(false)}
            selectedContactSync={selectedContactSync}
            onRun={(row) => runNowMutate(row)}
            onEdit={() => setOpenEditContactSync(true)}
            onDelete={() => setOpenDeleteContactSync(true)}
            onDeleteLabels={() => setOpenDeleteLabels(true)}
            onViewUserStatus={() => setOpenContactSyncUserStatus(true)}
          />
        ) : (
          <ContactSyncDetailsDialog
            open={openContactSyncDetails}
            handleClose={() => setOpenContactSyncDetails(false)}
            selectedContactSync={selectedContactSync}
          />
        )
      ) : null}
      {openContactSyncUserStatus && selectedContactSync ? (
        <ContactSyncUserStatusDialog
          open={openContactSyncUserStatus}
          handleClose={() => setOpenContactSyncUserStatus(false)}
          contactSyncId={selectedContactSync.id}
        />
      ) : null}
      {openRunLogs && selectedContactSync ? (
        <ContactSyncRunLogs
          open={openRunLogs}
          handleClose={() => setOpenRunLogs(false)}
          selectedContactSync={selectedContactSync}
        />
      ) : null}
    </SimpleBreadcrumbsPage>
  );
}
